import { RecommendationMatchReason } from 'src/app/core/domain/recommendation.model';

/**
 * SAN-1481 (P-5, OQ-18) — one short "why recommended" line built from the
 * backend's `matchReasons`, e.g.
 *   "Matches your sectors: FinTech, AgriTech · Also: SaaS business model"
 *   "Open to all sectors"
 * The closing-soon reason is shown as a separate chip, not in this line.
 * Returns '' when there is nothing to say (missing/empty reasons, old backend).
 */
export function recommendationReasonLine(reasons?: RecommendationMatchReason[] | null): string {
  if (!Array.isArray(reasons) || !reasons.length) {
    return '';
  }
  const parts: string[] = [];
  const also: string[] = [];
  const suffix = {
    business_model: 'business model',
    technology: 'technology',
    mentorship_area: 'mentorship',
  };
  reasons.forEach((reason) => {
    if (!reason) {
      return;
    }
    if (reason.type === 'sector' && reason.labels?.length) {
      parts.push(`Matches your sectors: ${reason.labels.join(', ')}`);
    } else if (reason.type === 'open_to_all') {
      parts.push('Open to all sectors');
    } else if (
      (reason.type === 'business_model' || reason.type === 'technology' || reason.type === 'mentorship_area') &&
      reason.labels?.length
    ) {
      also.push(`${reason.labels.join(', ')} ${suffix[reason.type]}`);
    }
  });
  if (also.length) {
    parts.push(`Also: ${also.join(', ')}`);
  }
  return parts.join(' · ');
}

/**
 * Closing-soon chip text ("Closes today" / "Closes in 1 day" /
 * "Closes in N days") when the reasons carry a closing_soon entry; '' otherwise.
 */
export function recommendationClosingChip(
  reasons?: RecommendationMatchReason[] | null,
  now: Date = new Date(),
): string {
  const closing = (reasons || []).find((reason) => reason?.type === 'closing_soon') as
    | { type: 'closing_soon'; closesAt: string }
    | undefined;
  if (!closing?.closesAt) {
    return '';
  }
  const closesAt = new Date(closing.closesAt).getTime();
  if (isNaN(closesAt)) {
    return '';
  }
  const days = Math.floor((closesAt - now.getTime()) / 86400000);
  if (days <= 0) {
    return 'Closes today';
  }
  return days === 1 ? 'Closes in 1 day' : `Closes in ${days} days`;
}
